import AddRegularDefault from 'mini-fluent-svg-icons/lib/custom-icons/AddRegularDefault';
import AlertRegular from 'mini-fluent-svg-icons/lib/icons/AlertRegular';
import AlertUnreadRegular from 'mini-fluent-svg-icons/lib/custom-icons/AlertUnreadRegular';
import classnames from 'classnames/bind';
import getSelectedFolderId from 'owa-mail-store/lib/utils/getSelectedFolderId';
import mailStore from 'owa-mail-store/lib/store/Store';
import openLeftNavPerfDatapointInitializer from 'mini-mail-list-store/lib/mutators/openLeftNavPerfDatapointInitialize';
import React, { useRef } from 'react';
import SearchRegular from 'mini-fluent-svg-icons/lib/icons/SearchRegular';
import strings from 'owa-strings/owa-mini-strings';
import styles from './MailSurfaceHeader.scss';
import { ActivityType } from 'mini-activity-pane-store/lib/store/schema/ActivityType';
import { areUnseenItemsPresent } from 'mini-activity-pane-store/lib/selectors/areUnseenItemsPresent';
import { areUnseenWhatsNewItemsPresent } from 'mini-activity-pane-store/lib/utils/areUnseenWhatsNewItemsPresent';
import { COMPOSE_MAIL_CLICKED } from 'mini-workflow-tracking/lib/constants/ListViewWorkflowConstants';
import { FloatingActionButton } from 'mini-floating-action-button';
import { generatePath, useNavigate } from 'mini-react-router-dom';
import { getFolderDisplayName } from 'owa-mail-store/lib/utils/getFolderDisplayName';
import { getFolderName } from 'mini-mail-list-store/lib/selectors/getFolderName';
import { getMailFilters } from '../utils/getMailFilters';
import { Header, HeaderTitle } from 'mini-header';
import { isWhatsNewFeatureEnabled } from 'mini-activity-pane-store/lib/utils/isWhatsNewFeatureEnabled';
import { LeftHeaderContent } from 'mini-surface-header-common/lib/components/LeftHeaderContent';
import { LOAD_ACTIVITY_PANE } from 'mini-workflow-tracking/lib/constants/ActivityPaneWorkflowConstants';
import { type MailRoutes, type MiscRoutes, type SearchRoutes } from 'mini-routes';
import { observer } from 'mobx-react-lite';
import { onMailListFilterClickCallback } from 'mini-mail-list-filter';
import { RightHeaderContent } from 'mini-surface-header-common/lib/components/RightHeaderContent';
import { shouldShowMailListHeaderForFolderId } from 'mini-mail-list-view/lib/utils/mailList/shouldShowMailListHeaderForFolderId';
import { shouldShowThemeHeroImage } from 'mini-mail-list-view/lib/utils/mailList/shouldShowThemeHeroImage';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { type UnifiedTelemetryEvent } from 'telemetry/lib/schema/UnifiedTelemetryEvent';
import { UTActiveAppView } from '@outlook/unified_telemetry';
import {
    LAUNCH_SEARCH,
    LAUNCH_MAIL_SEARCH,
} from 'mini-workflow-tracking/lib/constants/SearchWorkflowConstants';
import type { HeaderIconType } from 'mini-header';
import { isNonPrivateCloud } from 'mini-common-utils/lib/utils/isNonPrivateCloud';
import { isPublicCloudBusinessUser } from 'mini-common-utils/lib/utils/userEnvironmentUtils';

const cx = classnames.bind(styles);

interface MailSurfaceForwardedRefs {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    navigationButtonRef: React.RefObject<any>;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    searchButtonRef: React.RefObject<any>;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    composeButtonRef: React.RefObject<any>;
}

interface MailSurfaceHeaderProps {
    forwardedRefs?: MailSurfaceForwardedRefs;
}

export const MailSurfaceHeader = observer(function MailSurfaceHeader(
    props: MailSurfaceHeaderProps
) {
    const {
        forwardedRefs: {
            navigationButtonRef,
            searchButtonRef,
            composeButtonRef,
        } = {} as MailSurfaceForwardedRefs,
    } = props;
    const showThemeHeroImage = shouldShowThemeHeroImage();
    const navigate = useNavigate();

    // create the refs
    const mailFilterRef = useRef<HTMLDivElement>();

    const { folderTable } = mailStore.folders;
    const selectedFolderId = getSelectedFolderId();
    let folderName: string;

    if (selectedFolderId && folderTable.size > 0) {
        const selectedFolder = folderTable.get(selectedFolderId);

        // Fix for Bug: 34288
        // selectedFolder comes as null some times when pressing hardware back button multiple times.
        // Right now, doing a null check to avoid the error but created a WI 34429 on this.
        folderName = getFolderDisplayName(selectedFolder);
    }

    const onNavigationButtonClick = () => {
        openLeftNavPerfDatapointInitializer(true /*Initialising PerfDatapoint*/);
        navigate('sidepane');
    };

    // The right icons
    const onSearchButtonClick = () => {
        if (isNonPrivateCloud()) {
            signalWorkflowEvent(
                LAUNCH_SEARCH,
                { source: getFolderName() },
                {
                    isCore: true,
                    unifiedTelemetry: {
                        eventName: 'PerformSearch',
                        data: {
                            ActiveAppView: UTActiveAppView.Mail,
                        },
                    },
                }
            );
        } else {
            signalWorkflowEvent(LAUNCH_MAIL_SEARCH, { source: getFolderName() });
        }

        navigate('/search');
    };

    const onActivityIconClick = () => {
        signalWorkflowEvent(LOAD_ACTIVITY_PANE);
        const defaultView =
            !isPublicCloudBusinessUser() || areUnseenWhatsNewItemsPresent()
                ? ActivityType.WhatsNew
                : ActivityType.Reactions;
        navigate(generatePath('activity/:activityType', { activityType: defaultView }));
    };

    const onComposeButtonClick = () => {
        signalWorkflowEvent(COMPOSE_MAIL_CLICKED);
        navigate('compose/new');
    };

    const mailFilters = getMailFilters(selectedFolderId);
    const areNewNotificationsPresent = areUnseenItemsPresent();

    const rightIcons: HeaderIconType[] = [
        ...mailFilters.map(filter => ({
            icon: filter.icon,
            onClick: onMailListFilterClickCallback(filter.activeViewFilter, mailFilterRef),
            iconRef: mailFilterRef,
            ariaLabel: filter.ariaLabel,
            iconStyle: classnames(styles.icon, filter.isPressed && styles.isButtonPressed),
        })),
        (isWhatsNewFeatureEnabled() || isPublicCloudBusinessUser()) && {
            icon: areNewNotificationsPresent ? AlertUnreadRegular : AlertRegular,
            ariaLabel: areNewNotificationsPresent
                ? strings.activityNotificationsAriaLabel
                : strings.activityIconAriaLabel,
            onClick: onActivityIconClick,
        },
        {
            icon: SearchRegular,
            onClick: onSearchButtonClick,
            iconRef: searchButtonRef,
            ariaLabel: strings.searchButtonAriaLabel,
            id: 'search-icon',
            iconStyle: styles.icon,
        },
    ].filter(Boolean);

    return (
        <>
            <Header
                leftContent={
                    <LeftHeaderContent
                        navigationButtonRef={navigationButtonRef}
                        onOpenFolderPane={onNavigationButtonClick}
                        ariaLabel={strings.folderNavigationButtonAriaLabel}
                    />
                }
                rightContent={
                    <RightHeaderContent
                        rightIcons={rightIcons}
                        navigationButtonRef={navigationButtonRef}
                        ariaLabel={strings.folderNavigationButtonAriaLabel}
                    />
                }
                titleContent={
                    <HeaderTitle
                        title={folderName} // empty string to make sure HeaderTitle is rendered
                        titleStyles={styles.primaryTitle}
                    />
                }
                className={cx(styles.mailSurfaceHeader, {
                    [styles.hasSecondaryHeader]:
                        shouldShowMailListHeaderForFolderId(selectedFolderId) || showThemeHeroImage,
                })}
                titleAlign={'left'}
                transparent={showThemeHeroImage}
            />

            {/* Should be placed immediately after header for correct accessibility navigation order  */}
            <FloatingActionButton
                ref={composeButtonRef}
                icon={AddRegularDefault}
                onClick={onComposeButtonClick}
                ariaHasPopup={true}
                ariaLabel={strings.composeNewMailAriaLabel}
            />
        </>
    );
});
